import { useParams, useNavigate } from 'react-router-dom'
import { Badge, Button, Text, Stack } from '@mantine/core'
import { modals } from '@mantine/modals'
import {
  IconPhone,
  IconClock,
  IconCheck,
  IconExclamationMark,
  IconTrash,
} from '@tabler/icons-react'
import { formatDateTime } from '@medplum/core'
import { useSentFaxContent } from '@/hooks/useSentFaxContent'
import { useSentFaxDetail } from '@/hooks/useSentFaxDetail'
import { useDeleteSentFax } from '@/hooks/useDeleteSentFax'
import type { FaxSentItem } from '@/hooks/useFaxSent'
import { useEffect, useState } from 'react'
import { LinkFaxToTaskModal, type EditingFaxLink } from './LinkFaxToTaskModal'
import { LinkedTasksPanel } from './LinkedTasksPanel'

export function FaxSentDetailPage() {
  const { messageId } = useParams<{ messageId: string }>()

  if (!messageId) return null

  return <FaxSentContent messageId={messageId} />
}

type FaxSentContentProps = {
  messageId: string
}

function FaxSentContent({ messageId }: FaxSentContentProps) {
  const { data: item, isLoading: isLoadingDetail } = useSentFaxDetail(messageId)
  const navigate = useNavigate()
  const deleteFax = useDeleteSentFax(messageId)

  const { data: blob } = useSentFaxContent(messageId, item?.attachment?.id)

  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  // The link being edited, or null when the modal is creating a new one.
  const [editing, setEditing] = useState<EditingFaxLink | null>(null)

  useEffect(() => {
    if (!blob) return
    const url = URL.createObjectURL(blob)
    setBlobUrl(url)
    return () => {
      URL.revokeObjectURL(url)
      setBlobUrl(null)
    }
  }, [blob])

  if (isLoadingDetail || !item) return null

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Delete fax',
      children: 'Are you sure you want to delete this fax? This action cannot be undone.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        // `void`: react-router v7's navigate returns a promise, and the lint
        // rule this file must now satisfy rejects handing one to a void slot.
        deleteFax.mutate(undefined, { onSuccess: () => void navigate('/fax/sent') })
      },
    })
  }

  return (
    <div className="flex size-full flex-auto bg-gray-200">
      <div className="flex flex-auto flex-col items-center justify-center">
        {!!item.pageCount && blobUrl && (
          <embed
            key={item.attachment?.id}
            className="size-full"
            src={`${blobUrl}#navpanes=0&view=fitH`}
            type="application/pdf"
          />
        )}
      </div>
      <div className="flex w-xs flex-none flex-col gap-4 border-l border-gray-200 bg-white p-4">
        <StatusPanel item={item} />
        <LinkedTasksPanel
          messageId={messageId}
          onLink={() => {
            setEditing(null)
            setLinkOpen(true)
          }}
          onEdit={(link) => {
            setEditing(link)
            setLinkOpen(true)
          }}
        />
        <LinkFaxToTaskModal
          // Remounts per link so the form re-initialises from whichever row was
          // opened, instead of keeping the previous one's board, task and note.
          key={editing?.itemId ?? 'new'}
          opened={linkOpen}
          onClose={() => setLinkOpen(false)}
          editing={editing ?? undefined}
          messageId={messageId}
          // What the list shows for this fax, snapshotted onto the link so the
          // task's record survives the fax being deleted from RingCentral.
          recipient={item.recipient.name ?? item.recipient.phoneNumber}
          sentAt={item.sentAt}
        />
        <Button
          color="red"
          leftSection={<IconTrash size={16} />}
          loading={deleteFax.isPending}
          onClick={handleDelete}
        >
          Delete fax
        </Button>
      </div>
    </div>
  )
}

function StatusPanel({ item }: { item: FaxSentItem }) {
  return (
    <Stack gap="md">
      <div>
        <Text size="xs" c="dimmed" className="mb-1">
          Recipient
        </Text>
        <div className="flex items-center gap-2">
          <IconPhone size={14} className="text-gray-500" />
          <Text size="sm" fw={500}>
            {item.recipient.name ?? item.recipient.phoneNumber}
          </Text>
        </div>
      </div>

      <div>
        <Text size="xs" c="dimmed" className="mb-1">
          Status
        </Text>
        <DeliveryStatusBadge status={item.messageStatus} />
      </div>

      <div>
        <Text size="xs" c="dimmed" className="mb-1">
          Sent at
        </Text>
        <div className="flex items-center gap-2">
          <IconClock size={14} className="text-gray-500" />
          <Text size="sm">{formatDateTime(item.sentAt)}</Text>
        </div>
      </div>
    </Stack>
  )
}

function DeliveryStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'Sent':
    case 'Delivered':
      return (
        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
          {status}
        </Badge>
      )
    case 'Queued':
      return (
        <Badge color="yellow" variant="light" leftSection={<IconClock size={12} />}>
          Queued
        </Badge>
      )
    case 'SendingFailed':
      return (
        <Badge color="red" variant="light" leftSection={<IconExclamationMark size={12} />}>
          Failed
        </Badge>
      )
    default:
      return (
        <Badge color="gray" variant="light">
          {status}
        </Badge>
      )
  }
}
